import React from 'react';
import { IconButton, MenuItem, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import styles from './ThermographyPopup.module.css';
import { SIDE_LEFT, SIDE_RIGHT, formatDelta, formatTemp, isAsymmetric } from '../../pages/appointment/modules/thermography/thermography';
import { fieldKey, isInvalidTemp, liveDelta } from './thermographyForm';

/** Campo de temperatura em °C; o placeholder mostra o valor da avaliação anterior. */
export const TempField = ({ label, value, previous, onChange }) => (
  <TextField
    size="small"
    label={label}
    value={value || ''}
    placeholder={previous != null ? `ant. ${formatTemp(previous)}` : ''}
    onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, '').slice(0, 5))}
    error={isInvalidTemp(value)}
    inputProps={{ inputMode: 'decimal' }}
    InputLabelProps={{ shrink: true }}
    className={styles.tempField}
  />
);

/** Delta T ao vivo de um par, destacado acima do limiar. */
export const LiveDelta = ({ right, left }) => {
  const delta = liveDelta(right, left);
  if (delta == null) return <span className={styles.deltaEmpty}>ΔT —</span>;
  return <span className={`${styles.delta} ${isAsymmetric(delta) ? styles.deltaAlert : ''}`}>ΔT {formatDelta(delta)}</span>;
};

/**
 * Uma região do catálogo, recolhível: cada local com direito e esquerdo
 * (e o delta T) ou um valor único. `previous`: valores da avaliação anterior
 * por `fieldKey`, mostrados como dica.
 */
export const RegionSection = ({ region, values, previous, expanded, onToggle, onChange }) => {
  const filled = region.sites.filter((site) => (site.bilateral ? [SIDE_RIGHT, SIDE_LEFT] : [null])
    .some((side) => values[fieldKey(site.key, side)])).length;

  return (
    <div className={styles.region}>
      <button type="button" className={styles.regionHeader} onClick={onToggle} aria-expanded={expanded}>
        <span className={styles.regionTitle}>{region.label}</span>
        {filled > 0 && <span className={styles.regionCount}>{filled} {filled === 1 ? 'local' : 'locais'}</span>}
        <ExpandMoreIcon className={`${styles.regionIcon} ${expanded ? styles.regionIconOpen : ''}`} />
      </button>

      {expanded && (
        <div className={styles.siteList}>
          {region.sites.map((site) => (
            <div key={site.key} className={styles.siteRow}>
              <span className={styles.siteLabel}>{site.label}</span>
              {site.bilateral ? (
                <div className={styles.siteInputs}>
                  <TempField
                    label="Direito"
                    value={values[fieldKey(site.key, SIDE_RIGHT)]}
                    previous={previous[fieldKey(site.key, SIDE_RIGHT)]}
                    onChange={(text) => onChange(fieldKey(site.key, SIDE_RIGHT), text)}
                  />
                  <TempField
                    label="Esquerdo"
                    value={values[fieldKey(site.key, SIDE_LEFT)]}
                    previous={previous[fieldKey(site.key, SIDE_LEFT)]}
                    onChange={(text) => onChange(fieldKey(site.key, SIDE_LEFT), text)}
                  />
                  <LiveDelta right={values[fieldKey(site.key, SIDE_RIGHT)]} left={values[fieldKey(site.key, SIDE_LEFT)]} />
                </div>
              ) : (
                <div className={styles.siteInputs}>
                  <TempField
                    label="°C"
                    value={values[fieldKey(site.key, null)]}
                    previous={previous[fieldKey(site.key, null)]}
                    onChange={(text) => onChange(fieldKey(site.key, null), text)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Um local livre: nome, se tem dois lados, temperatura(s) e remover. */
export const CustomSiteRow = ({ row, onChange, onRemove }) => (
  <div className={styles.customRow}>
    <TextField
      size="small"
      label="Local"
      value={row.label}
      onChange={(e) => onChange({ label: e.target.value.slice(0, 60) })}
      className={styles.customLabel}
    />
    <TextField
      select
      size="small"
      label="Lados"
      value={row.bilateral ? 'both' : 'single'}
      onChange={(e) => onChange({ bilateral: e.target.value === 'both' })}
      className={styles.customMode}
    >
      <MenuItem value="single">Único</MenuItem>
      <MenuItem value="both">Direito e esquerdo</MenuItem>
    </TextField>
    {row.bilateral ? (
      <>
        <TempField label="Direito" value={row.right} onChange={(text) => onChange({ right: text })} />
        <TempField label="Esquerdo" value={row.left} onChange={(text) => onChange({ left: text })} />
        <LiveDelta right={row.right} left={row.left} />
      </>
    ) : (
      <TempField label="°C" value={row.value} onChange={(text) => onChange({ value: text })} />
    )}
    <IconButton size="small" aria-label="Remover local" onClick={onRemove}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </div>
);
